// A PIPE RUN KEEPS ITS COLOUR (3.18.0). Owner, 2026-10-21: "I need to be able to set the colour of components". A
// person colours a run by its service (for example suction green, return blue). When the person then draws on from
// that run, the new part takes the colour of the part it starts on:
//   - a new pipe on the end of a pipe, a bend, a tee, a reducer or a check valve takes the line colour of that part;
//   - a fitting placed on a pipe end (right-click the end) takes the line colour of the pipe.
// A pump and a lint pot do not give their colour: a coloured pump is a mark on the pump, not on the run.
// The standard colour (OUTLINE) gives nothing, so a run with no colour stays as it was.
// Pure: no store, no UI. tests/componentColour.test.ts covers it.
import type { Doc, Entity, Style } from "../doc/entities";
import { OUTLINE } from "../library/fittings";

/** True for the parts of a run: a pipe, a bend, a tee, a reducer, a check valve. Not a pump, not a lint pot. */
function isRunPart(e: Entity | undefined): boolean {
  return e?.type === "group" && (!!e.meta?.pipe || (!!e.meta?.fitting && !e.meta?.lintpot && !e.meta?.pump));
}

/** The line colour most of the solid lines of run part `id` have, when it is not the standard colour; else null. */
export function runColourOf(doc: Doc, id: string): string | null {
  const g = doc.entities[id];
  if (!g || g.type !== "group" || !isRunPart(g)) return null;
  const n = new Map<string, number>();
  for (const c of g.children) {
    const e = doc.entities[c];
    if (!e || !("style" in e)) continue;
    const s = (e as Entity & { style: Style }).style;
    if (s.dash !== "solid") continue;
    const k = s.strokeColor.toLowerCase();
    n.set(k, (n.get(k) ?? 0) + 1);
  }
  let best: string | null = null;
  for (const [c, k] of n) if (best === null || k > n.get(best)!) best = c;
  return best && best !== OUTLINE.strokeColor.toLowerCase() ? best : null;
}

/** The colour of the first run part that an anchor list names (port anchors only), or null. */
export function runColourAt(doc: Doc, anchors: ReadonlyArray<{ kind: string; ownerId?: string }>): string | null {
  for (const a of anchors) {
    if (a.kind !== "port" || !a.ownerId) continue;
    const c = runColourOf(doc, a.ownerId);
    if (c) return c;
  }
  return null;
}

/** `style` with the line colour `colour` (null: `style` as it is). */
export function withLineColour(style: Style, colour: string | null): Style {
  return colour ? { ...style, strokeColor: colour } : style;
}

/** Library parts with every line in `colour` (null: the parts as they are). Groups and texts do not change. */
export function partsInColour<T extends Entity>(entities: T[], colour: string | null): T[] {
  if (!colour) return entities;
  return entities.map((e) => ("style" in e ? { ...e, style: withLineColour((e as T & { style: Style }).style, colour) } : e));
}
